import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { useParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import SkeletonLoader from '../components/common/SkeletonLoader';
import usersService from '../api/users';
import MessageItem from '../components/feed/MessageItem';
import { FaTrash, FaTimes, FaExclamationTriangle } from 'react-icons/fa';
import { toast } from 'react-toastify';
import messagesService from '../api/messages';

// Animaciones para transiciones más suaves
const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const ProfileContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px 15px 40px;
  margin-left: 70px;
  width: calc(100% - 70px);
  box-sizing: border-box;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: ${props => props.theme.body};
  animation: ${fadeIn} 0.3s ease;
  
  @media (max-width: 992px) {
    margin-left: 60px;
    width: calc(100% - 60px);
    padding: 15px 12px 30px;
  }
  
  @media (max-width: 768px) {
    margin-left: 0;
    margin-bottom: 56px;
    padding: 12px 10px 25px;
    width: 100%;
  }
  
  @media (max-width: 480px) {
    padding: 10px 8px 20px;
  }
`;

const ProfileHeader = styled.div`
  background-color: ${props => props.theme.card};
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: ${props => props.theme.shadow};
  width: 100%;
  box-sizing: border-box;
  animation: ${slideIn} 0.4s ease;
  
  @media (max-width: 768px) {
    padding: 15px;
  }
  
  @media (max-width: 480px) {
    padding: 12px;
  }
`;

const ProfileInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const ProfileAvatar = styled.div`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background-color: ${props => props.theme.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 15px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  
  @media (max-width: 480px) {
    width: 80px;
    height: 80px;
  }
`;

const FrogIcon = styled.div`
  font-size: 50px;
  color: white;
  
  @media (max-width: 480px) {
    font-size: 40px;
  }
`;

const ProfileName = styled.h1`
  margin: 10px 0;
  font-size: 24px;
  color: ${props => props.theme.text};
  
  @media (max-width: 480px) {
    font-size: 22px;
  }
`;

const ProfileUsername = styled.p`
  color: ${props => props.theme.textSecondary};
  margin-bottom: 10px;
  font-size: 16px;
`;

const ProfileEmail = styled.p`
  color: ${props => props.theme.textSecondary};
  margin-bottom: 15px;
  font-size: 14px;
`;

const PostCount = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 10px;
  padding: 8px 16px;
  background-color: ${props => props.theme.backgroundSecondary};
  border-radius: 20px;
  
  span:first-child {
    font-size: 18px;
    font-weight: bold;
    color: ${props => props.theme.primary};
  }
  
  span:last-child {
    font-size: 12px;
    color: ${props => props.theme.textSecondary};
  }
`;

const ContentSection = styled.div`
  background-color: ${props => props.theme.card};
  border-radius: 8px;
  padding: 20px;
  box-shadow: ${props => props.theme.shadow};
  margin-bottom: 20px;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  transition: opacity 0.2s ease;
  
  h2 {
    margin-top: 0;
    margin-bottom: 20px;
    color: ${props => props.theme.text};
    font-size: 20px;
    
    @media (max-width: 768px) {
      font-size: 18px;
      margin-bottom: 16px;
    }
    
    @media (max-width: 480px) {
      font-size: 16px;
      margin-bottom: 12px;
    }
  }
  
  @media (max-width: 768px) {
    padding: 15px;
    border-radius: 6px;
  }
  
  @media (max-width: 480px) {
    padding: 12px;
    border-radius: 4px;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 30px 20px;
  color: ${props => props.theme.textSecondary};
  font-style: italic;
  flex-grow: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${fadeIn} 0.5s ease;
  font-size: 15px;
  
  @media (max-width: 768px) {
    padding: 25px 15px;
    font-size: 14px;
  }
  
  @media (max-width: 480px) {
    padding: 20px 10px;
    font-size: 13px;
  }
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 30px;
  color: ${props => props.theme.error};
  background-color: ${props => props.theme.errorBg};
  border-radius: 8px;
  margin-top: 20px;
  animation: ${fadeIn} 0.5s ease;
`;

const DeleteButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  background-color: ${props => props.theme.error};
  color: white;
  border: none;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: 0.9;
  z-index: 10;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  
  &:hover {
    background-color: ${props => props.theme.errorDark};
    transform: scale(1.05);
    opacity: 1;
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.3);
  }

  &:active {
    transform: scale(0.95);
  }
  
  @media (max-width: 992px) {
    width: 28px;
    height: 28px;
    top: 7px;
    right: 7px;
  }
  
  @media (max-width: 768px) {
    width: 26px;
    height: 26px;
    top: 6px;
    right: 6px;
  }
  
  @media (max-width: 480px) {
    width: 24px;
    height: 24px;
    top: 5px;
    right: 5px;
  }

  @media (max-width: 360px) {
    width: 22px;
    height: 22px;
    top: 4px;
    right: 4px;
  }
`;

// Spinner para carga inicial
const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px 0;
  width: 100%;
  background-color: ${props => props.theme.card};
  opacity: 0.8;
  transition: opacity 0.2s ease;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 5;

  &:after {
    content: "";
    width: 32px;
    height: 32px;
    border: 2px solid ${props => props.theme.divider};
    border-top: 2px solid ${props => props.theme.primary};
    border-radius: 50%;
    animation: ${spin} 0.8s linear infinite;
  }

  @media (max-width: 768px) {
    &:after {
      width: 28px;
      height: 28px;
    }
  }
  
  @media (max-width: 480px) {
    &:after {
      width: 24px;
      height: 24px;
      border-width: 1.5px;
    }
  }
`;

// Contenedor para el spinner de perfil
const ProfileLoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
  width: 100%;
  position: relative;
`;

// Spinner pequeño para carga del perfil
const ProfileLoadingSpinner = styled.div`
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  
  &:after {
    content: "";
    width: 40px;
    height: 40px;
    border: 3px solid ${props => props.theme.divider};
    border-top: 3px solid ${props => props.theme.primary};
    border-radius: 50%;
    animation: ${spin} 1s linear infinite;
  }
`;

// Actualizar también el componente MessagesContainer para que sea consistente
const MessagesContainer = styled.div`
  overflow-y: visible;
  flex-grow: 1;
  padding-right: 5px;
  position: relative;
  min-height: 300px;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${props => props.theme.divider};
    border-radius: 10px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.textSecondary};
    border-radius: 10px;
  }
  
  scrollbar-width: thin;
  scrollbar-color: ${props => props.theme.textSecondary} ${props => props.theme.divider};
  
  @media (max-width: 768px) {
    min-height: 250px;
    padding-right: 3px;
  }
  
  @media (max-width: 480px) {
    min-height: 200px;
    padding-right: 2px;
    
    &::-webkit-scrollbar {
      width: 4px;
    }
  }
`;

// Animación para los elementos entrantes con retraso (más sutil)
const AnimatedMessageContainer = styled.div`
  position: relative;
  transition: opacity 0.15s ease;
  opacity: 1;
  margin-bottom: 16px;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 12px;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 8px;
  }
`;

// Añadir componentes para el modal de confirmación
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${props => props.theme.overlay};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
`;

const ModalContent = styled.div`
  background-color: ${props => props.theme.card};
  border-radius: 8px;
  box-shadow: ${props => props.theme.shadow};
  width: 90%;
  max-width: 400px;
  padding: 0;
  overflow: hidden;
  animation: modalFadeIn 0.3s ease;
  
  @keyframes modalFadeIn {
    from {
      opacity: 0;
      transform: translateY(-20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid ${props => props.theme.divider};
  
  h3 {
    margin: 0;
    color: ${props => props.theme.text};
    font-size: 18px;
    font-weight: 600;
  }
`;

const ModalBody = styled.div`
  padding: 20px;
  text-align: center;
  color: ${props => props.theme.text};
  
  p {
    margin: 8px 0;
    line-height: 1.5;
  }
  
  p:last-child {
    color: ${props => props.theme.textSecondary};
    font-size: 14px;
    margin-top: 12px;
  }
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 16px 20px;
  border-top: 1px solid ${props => props.theme.divider};
  gap: 12px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.textSecondary};
  font-size: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border-radius: 50%;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: ${props => props.theme.buttonLight};
  }
`;

const CancelButton = styled.button`
  background-color: ${props => props.theme.buttonLight};
  color: ${props => props.theme.text};
  border: none;
  border-radius: 6px;
  padding: 10px 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: ${props => props.theme.buttonLightHover};
  }
`;

const DeleteConfirmButton = styled.button`
  background-color: #e74c3c;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 10px 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #c0392b;
  }
`;

const WarningIcon = styled.div`
  color: #e74c3c;
  font-size: 40px;
  margin-bottom: 16px;
  
  svg {
    width: 40px;
    height: 40px;
  }
`;

function Profile() {
  const { username } = useParams();
  const { isAuthenticated, user } = useAuth();
  
  const [profileData, setProfileData] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);
  
  // Refs para el scroll infinito
  const observerRef = useRef(null);
  const observerTarget = useRef(null);
  
  // Añadir estado para el modal de confirmación
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState(null);
  
  // Determinar si el perfil pertenece al usuario actual
  const isOwnProfile = isAuthenticated && user && 
    ((!username && user.username) || (username === user.username));
  
  // Función para cargar el perfil del usuario
  const fetchProfileData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Determinar qué username usar
      const targetUsername = username || (user ? user.username : null);
      
      if (!targetUsername) {
        setError("No se pudo determinar el usuario a mostrar");
        return;
      }
      
      const profileResponse = await usersService.getUserProfile(targetUsername);
      
      // Modificar para adaptarse a la estructura de respuesta actual
      if (profileResponse) {
        // Si la respuesta tiene un campo user, usarlo
        if (profileResponse.user) {
          setProfileData({
            name: "Universitario Anónimo",
            username: profileResponse.user.username,
            email: profileResponse.user.email,
            posts: profileResponse.message_count || 0
          });
        } else {
          // Si no, asumir que la respuesta es el usuario directamente
          setProfileData({
            name: "Universitario Anónimo",
            username: profileResponse.username,
            email: profileResponse.email,
            posts: profileResponse.message_count || 0
          });
        }
      }
    } catch (error) {
      console.error('Error fetching profile data:', error);
      setError("No se pudo cargar el perfil del usuario");
    } finally {
      setLoading(false);
    }
  }, [username, user]);
  
  // Función para cargar los mensajes del usuario
  const fetchUserMessages = useCallback(async (pageNum = 1, append = false) => {
    try {
      if (pageNum === 1) {
        setLoadingMessages(true);
      } else {
        setLoadingMore(true);
      }
      
      // Determinar qué username usar
      const targetUsername = username || (user ? user.username : null);
      
      if (!targetUsername) {
        return;
      }
      
      const messagesResponse = await usersService.getUserMessages(targetUsername, pageNum, 10);
      
      if (messagesResponse && messagesResponse.length > 0) {
        // Formatear los mensajes para asegurar consistencia en los campos
        const formattedMessages = messagesResponse.map(msg => ({
          id: msg.id,
          text: msg.text,
          image: msg.image,
          author: {
            username: msg.username || "Usuario"
          },
          author_id: msg.user_id,
          // Asegurar que created_at esté correctamente formateado
          created_at: msg.timestamp || msg.created_at,
          likes_count: msg.likes || msg.likes_count || 0,
          comments_count: msg.comments || msg.comments_count || 0,
          shares_count: msg.shares || msg.shares_count || 0,
          liked: Boolean(msg.liked),
          shared: Boolean(msg.shared)
        }));
        
        // Verificar el estado de like para cada mensaje si el usuario está autenticado
        let messagesWithLikeStatus = formattedMessages;
        
        if (isAuthenticated && user) {
          // Procesar mensajes y verificar likes en paralelo
          messagesWithLikeStatus = await Promise.all(formattedMessages.map(async (msg) => {
            // Verificar el estado de like para este mensaje
            let likeStatus = { liked: msg.liked || false };
            try {
              likeStatus = await messagesService.checkLikeStatus(msg.id);
            } catch (error) {
              console.error(`Error checking like status for message ${msg.id}:`, error);
            }
            
            return {
              ...msg,
              liked: likeStatus.liked
            };
          }));
        }
        
        if (append) {
          setMessages(prev => [...prev, ...messagesWithLikeStatus]);
        } else {
          setMessages(messagesWithLikeStatus);
        }
        
        // Verificar si hay más mensajes para cargar
        setHasMore(messagesResponse.length === 10);
      }
    } catch (error) {
      console.error('Error fetching user messages:', error);
      toast.error("Error al cargar los mensajes");
    } finally {
      setLoadingMessages(false);
      setLoadingMore(false);
    }
  }, [username, user, isAuthenticated]);
  
  // Cargar más mensajes - Mover esta definición antes del useEffect y envolverla en useCallback
  const loadMoreMessages = useCallback(() => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchUserMessages(nextPage, true);
  }, [page, fetchUserMessages]);
  
  // Configurar el Intersection Observer para scroll infinito
  useEffect(() => {
    // Desconectar el observer anterior si existe
    if (observerRef.current) {
      observerRef.current.disconnect();
    }
    
    // Crear un nuevo observer
    observerRef.current = new IntersectionObserver(entries => {
      const [entry] = entries;
      if (entry.isIntersecting && hasMore && !loadingMore && !loadingMessages) {
        loadMoreMessages();
      }
    }, {
      rootMargin: '100px',
    });
    
    // Observar el elemento target si existe
    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observerRef.current.observe(currentTarget);
    }
    
    // Limpiar al desmontar
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [hasMore, loadingMore, loadingMessages, loadMoreMessages]);
  
  // Modificar la función para eliminar un mensaje
  const handleDeleteMessage = (messageId) => {
    if (!isAuthenticated) return;
    
    // Mostrar el modal en lugar de window.confirm
    setMessageToDelete(messageId);
    setShowDeleteModal(true);
  };
  
  // Función para confirmar la eliminación
  const confirmDelete = async () => {
    try {
      await usersService.deleteMessage(messageToDelete);
      
      // Invalidar cache para que el mensaje eliminado no se muestre en otros componentes
      if (messagesService.invalidateMessageCache) {
        messagesService.invalidateMessageCache();
      }
      
      // Actualizar la lista de mensajes
      setMessages(prev => prev.filter(msg => msg.id !== messageToDelete));
      
      // Actualizar el contador de posts
      if (profileData) {
        setProfileData(prev => ({
          ...prev,
          posts: Math.max(0, prev.posts - 1)
        }));
      }
      
      toast.success("Mensaje eliminado correctamente");
    } catch (error) {
      console.error('Error deleting message:', error);
      toast.error("Error al eliminar el mensaje");
    } finally {
      // Cerrar el modal
      setShowDeleteModal(false);
      setMessageToDelete(null);
    }
  };
  
  // Función para cancelar la eliminación
  const cancelDelete = () => {
    setShowDeleteModal(false);
    setMessageToDelete(null);
  };
  
  // Cargar datos al montar el componente
  useEffect(() => {
    fetchProfileData();
  }, [fetchProfileData]);
  
  // Scroll hacia arriba cuando se carga un nuevo perfil
  useEffect(() => {
    // Scroll simple con comportamiento auto
    window.scrollTo({
      top: 0,
      behavior: 'auto'
    });
  }, [username]);
  
  // Cargar mensajes cuando se carga el perfil
  useEffect(() => {
    if (!loading && profileData) {
      fetchUserMessages(1, false);
    }
  }, [loading, profileData, fetchUserMessages]);
  
  // Mostrar un estado de carga mejorado mientras se cargan los datos
  if (loading) {
    return (
      <ProfileContainer>
        <ProfileLoadingContainer>
          <ProfileLoadingSpinner />
        </ProfileLoadingContainer>
      </ProfileContainer>
    );
  }
  
  if (error) {
    return (
      <ProfileContainer>
        <ErrorMessage>{error}</ErrorMessage>
      </ProfileContainer>
    );
  }

  if (!profileData) {
    return (
      <ProfileContainer>
        <ErrorMessage>Perfil no encontrado</ErrorMessage>
      </ProfileContainer>
    );
  }

  return (
    <ProfileContainer>
      {/* Añadir el modal de confirmación */}
      {showDeleteModal && (
        <ModalOverlay>
          <ModalContent>
            <ModalHeader>
              <h3>Confirmar eliminación</h3>
              <CloseButton onClick={cancelDelete}>
                <FaTimes />
              </CloseButton>
            </ModalHeader>
            <ModalBody>
              <WarningIcon>
                <FaExclamationTriangle />
              </WarningIcon>
              <p>¿Estás seguro de que deseas eliminar esta publicación?</p>
              <p>Esta acción no se puede deshacer.</p>
            </ModalBody>
            <ModalFooter>
              <CancelButton onClick={cancelDelete}>
                Cancelar
              </CancelButton>
              <DeleteConfirmButton onClick={confirmDelete}>
                Eliminar
              </DeleteConfirmButton>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}
      
      <ProfileHeader>
        <ProfileInfo>
          <ProfileAvatar>
            <FrogIcon>🐸</FrogIcon>
          </ProfileAvatar>
          <ProfileName>{profileData.name}</ProfileName>
          <ProfileUsername>@{profileData.username}</ProfileUsername>
          <ProfileEmail>{profileData.email}</ProfileEmail>
          
          <PostCount>
            <span>{profileData.posts}</span>
            <span>Publicaciones</span>
          </PostCount>
        </ProfileInfo>
      </ProfileHeader>
      
      <ContentSection>
        <h2>Publicaciones</h2>
        <MessagesContainer>
          {loadingMessages && <LoadingSpinner />}
          
          {messages.length > 0 ? (
            <>
              {messages.map((message, index) => (
                <AnimatedMessageContainer 
                  key={message.id}
                >
                  {isOwnProfile && (
                    <DeleteButton 
                      onClick={() => handleDeleteMessage(message.id)}
                      title="Eliminar mensaje"
                    >
                      <FaTrash size={window.innerWidth <= 360 ? 12 : 
                                window.innerWidth <= 480 ? 13 :
                                window.innerWidth <= 768 ? 14 : 15} />
                    </DeleteButton>
                  )}
                  <MessageItem 
                    message={{
                      ...message,
                      likes_count: message.likes_count || message.likes || 0,
                      comments_count: message.comments_count || message.comments || 0,
                      shares_count: message.shares_count || message.shares || 0
                    }} 
                    onHashtagClick={() => {}}
                  />
                </AnimatedMessageContainer>
              ))}
              
              <div ref={observerTarget}>
                {hasMore && loadingMore && (
                  <SkeletonLoader type="message" count={2} />
                )}
              </div>
            </>
          ) : !loadingMessages && (
            <EmptyState>
              Este usuario aún no ha realizado publicaciones.
            </EmptyState>
          )}
        </MessagesContainer>
      </ContentSection>
    </ProfileContainer>
  );
}

export default Profile;
